import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from 'react-hot-toast';
import AuthGuard from '@/components/AuthGuard';
import Layout from '@/components/Layout';
import LoginPage from '@/pages/LoginPage';
import SsoCallbackPage from '@/pages/SsoCallbackPage';
import DashboardPage from '@/pages/DashboardPage';
import UsersPage from '@/pages/UsersPage';
import OrganizationsPage from '@/pages/OrganizationsPage';
import RolesPermissionsPage from '@/pages/RolesPermissionsPage';
import AuditLogsPage from '@/pages/AuditLogsPage';
import WebhooksPage from '@/pages/WebhooksPage';
import ApiKeysPage from '@/pages/ApiKeysPage';
import EmailTemplatesPage from '@/pages/EmailTemplatesPage';
import SettingsPage from '@/pages/SettingsPage';
// CSS is now imported in main.tsx

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <Router>
        <div className="App">
          <Routes>
            <Route path="/login" element={<LoginPage />} />
            <Route path="/sso/callback" element={<SsoCallbackPage />} />
            <Route
              path="/*"
              element={
                <AuthGuard>
                  <Layout>
                    <Routes>
                      <Route path="/" element={<Navigate to="/dashboard" replace />} />
                      <Route path="/dashboard" element={<DashboardPage />} />
                      <Route
                        path="/users/*"
                        element={
                          <AuthGuard requiredPermissions={['user:read', 'admin:*']}>
                            <Routes>
                              <Route path="/" element={<UsersPage />} />
                              <Route path="/:userId" element={<UsersPage />} />
                            </Routes>
                          </AuthGuard>
                        }
                      />
                      <Route
                        path="/organizations/*"
                        element={
                          <AuthGuard requiredPermissions={['org:read', 'admin:*']}>
                            <Routes>
                              <Route path="/" element={<OrganizationsPage />} />
                              <Route path="/:orgId" element={<OrganizationsPage />} />
                            </Routes>
                          </AuthGuard>
                        }
                      />
                      <Route
                        path="/roles"
                        element={
                          <AuthGuard requiredPermissions={['role:read', 'admin:*']}>
                            <RolesPermissionsPage />
                          </AuthGuard>
                        }
                      />
                      <Route
                        path="/audit-logs"
                        element={
                          <AuthGuard requiredPermissions={['audit:read', 'admin:*']}>
                            <AuditLogsPage />
                          </AuthGuard>
                        }
                      />
                      <Route
                        path="/webhooks"
                        element={
                          <AuthGuard requiredPermissions={['system:read', 'admin:*']}>
                            <WebhooksPage />
                          </AuthGuard>
                        }
                      />
                      <Route
                        path="/api-keys"
                        element={
                          <AuthGuard requiredPermissions={['admin:*']}>
                            <ApiKeysPage />
                          </AuthGuard>
                        }
                      />
                      <Route
                        path="/email-templates"
                        element={
                          <AuthGuard requiredPermissions={['system:read', 'admin:*']}>
                            <EmailTemplatesPage />
                          </AuthGuard>
                        }
                      />
                      <Route
                        path="/settings"
                        element={
                          <AuthGuard requiredPermissions={['system:read', 'admin:*']}>
                            <SettingsPage />
                          </AuthGuard>
                        }
                      />
                    </Routes>
                  </Layout>
                </AuthGuard>
              }
            />
          </Routes>
          <Toaster
            position="top-right"
            toastOptions={{
              duration: 4000,
              style: {
                background: '#363636',
                color: '#fff',
              },
            }}
          />
        </div>
      </Router>
    </QueryClientProvider>
  );
}

export default App;
